/**
 * frontend/src/features/telephony/services/telephonyHelpers.js
 * ------------------------------------------------------------
 * Small pure helpers shared across the telephony settings UI.
 */

/** Safely pull a human-readable message out of an Axios error. */
export function getErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail)
  return err?.message || fallback
}

/**
 * Describe a cached JWT's freshness for a status badge.
 * @param {string|null} expiresAt ISO timestamp
 * @returns {{ state: 'valid'|'expiring'|'expired'|'none', label: string }}
 */
export function tokenStatus(expiresAt) {
  if (!expiresAt) return { state: 'none', label: 'Not minted' }
  const exp = new Date(expiresAt).getTime()
  if (Number.isNaN(exp)) return { state: 'none', label: 'Unknown' }
  const msLeft = exp - Date.now()
  if (msLeft <= 0) return { state: 'expired', label: 'Expired — will re-mint' }

  const hours = Math.floor(msLeft / 3_600_000)
  const mins = Math.floor((msLeft % 3_600_000) / 60_000)
  const human = hours >= 1 ? `${hours}h ${mins}m` : `${mins}m`
  if (msLeft < 3_600_000) return { state: 'expiring', label: `Expires in ${human}` }
  return { state: 'valid', label: `Valid for ${human}` }
}

/** Tailwind classes for a token status pill. */
export function tokenBadgeClasses(state) {
  switch (state) {
    case 'valid':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200'
    case 'expiring':
      return 'bg-amber-50 text-amber-800 border-amber-200'
    case 'expired':
      return 'bg-red-50 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

// Module-level handle to the currently-active interceptor's restore fn,
// so destroy() can force-clean it if the softphone is turned off mid-dial.
let activeRestore = null

/**
 * Run `action` (which triggers the Exotel SDK's async MakeCall) while a scoped,
 * self-healing fetch interceptor is active. The interceptor injects data into a
 * single matched outgoing request, then restores the real window.fetch
 * IMMEDIATELY — it never waits for the HTTP response or for the call to connect.
 *
 * Restoration is guaranteed by three independent nets:
 *   1. Single-shot: the instant the matched request is dispatched.
 *   2. catch: if `action` throws synchronously before any fetch happens.
 *   3. Watchdog: if the matched request is never dispatched within timeoutMs.
 *
 * NOTE: MakeCall is async and returns BEFORE it sends its request, so we must
 * not restore in a `finally` right after action() returns — that would tear the
 * patch down too early and app_id would never be injected.
 *
 * @param {object}        cfg
 * @param {string|RegExp} cfg.urlPattern       Endpoint to match (substring or RegExp).
 * @param {function}      cfg.transformRequest (input, options) => ({ url, options }).
 * @param {function}      cfg.action           The call to run (e.g. () => sdk.MakeCall(...)).
 * @param {number}        [cfg.timeoutMs=5000] Watchdog timeout.
 */
export function runWithScopedFetchInterceptor({
  urlPattern,
  transformRequest = (input, options) => ({ url: input, options }),
  action,
  timeoutMs = 5000,
}) {
  // Reentrancy guard: never stack a second patch (it would capture an
  // already-patched fetch as "original" and lose the native one).
  if (activeRestore) {
    return action()
  }

  const originalFetch = window.fetch
  let restored = false
  let watchdog = null

  const restore = () => {
    if (restored) return
    restored = true
    window.fetch = originalFetch
    activeRestore = null
    if (watchdog) clearTimeout(watchdog)
  }

  const matches = (input) => {
    let urlStr = ''
    if (typeof input === 'string') urlStr = input
    else if (typeof URL !== 'undefined' && input instanceof URL) urlStr = input.href
    else if (input && typeof input.url === 'string') urlStr = input.url // Request obj
    if (!urlStr) return false
    return urlPattern instanceof RegExp
      ? urlPattern.test(urlStr)
      : urlStr.includes(urlPattern)
  }

  activeRestore = restore

  window.fetch = function (input, options) {
    // Net 1 — single-shot restore on first match, BEFORE sending the request.
    if (!restored && matches(input)) {
      const { url, options: opts } = transformRequest(input, options)
      restore()
      return originalFetch.call(this, url, opts)
    }
    // Everything else passes straight through, untouched.
    return originalFetch.apply(this, arguments)
  }

  // Net 3 — watchdog.
  watchdog = setTimeout(restore, timeoutMs)

  try {
    return action()
  } catch (err) {
    // Net 2 — only reached if action() throws SYNCHRONOUSLY, before any fetch.
    restore()
    throw err
  }
}

/** Force-clean any lingering interceptor (called from SDK destroy()). */
export function forceRestoreFetchInterceptor() {
  if (activeRestore) activeRestore()
}

/**
 * Calculate the millisecond delay to schedule a proactive token auto-renewal.
 * Typically renews 5 minutes (300,000 ms) before expiration, or at 90% of TTL for short-lived tokens.
 *
 * @param {string|null} expiresAt ISO 8601 timestamp string
 * @param {number} [ttlSeconds=86400] Total validity in seconds
 * @returns {number} Milliseconds to delay before refreshing (minimum 15 seconds)
 */
export function calculateTokenRenewDelay(expiresAt, ttlSeconds = 86400) {
  if (expiresAt) {
    const expTime = new Date(expiresAt).getTime()
    if (!Number.isNaN(expTime)) {
      const msLeft = expTime - Date.now()
      // Renew 5 minutes (300,000 ms) before expiry, or at 90% of msLeft if close
      const bufferMs = Math.min(300_000, Math.max(15_000, Math.floor(msLeft * 0.1)))
      const targetDelay = msLeft - bufferMs
      return Math.max(15_000, targetDelay)
    }
  }
  const ttlMs = (ttlSeconds || 86400) * 1000
  return Math.max(15_000, ttlMs - 300_000)
}

/**
 * Determine if an error returned by Exotel or the backend indicates an expired or unauthorized token.
 *
 * @param {any} err
 * @returns {boolean}
 */
export function isTokenExpiredError(err) {
  if (!err) return false
  const str = typeof err === 'string' ? err : JSON.stringify(err)
  const lower = str.toLowerCase()
  return (
    lower.includes('401') ||
    lower.includes('unauthorized') ||
    lower.includes('token expired') ||
    lower.includes('invalid token') ||
    lower.includes('jwt expired') ||
    lower.includes('auth failed') ||
    lower.includes('authentication failed')
  )
}

